import React from 'react';
import classes from 'classnames';
import { FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import classNames from './LoginForm.module.scss';
import { LoginValues } from '@/types/login';
import AppInput from '../AppInput/AppInput';
import TheButton from '../TheButton/TheButton';
import AppLoaderCircle from '../AppLoaderCircle';
import { PASSWORD_REGEX } from '@/utils/common';

export type RegistrationFormProps = {
  emitSubmit?: (values: LoginValues, formikHelpers: FormikHelpers<LoginValues>) => void;
  styles: any;
  loading?: boolean;
};

const RegistrationForm = ({ emitSubmit, styles, loading }: RegistrationFormProps) => {
  const { t } = useTranslation();

  const RegistrationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required(t('settings.add_students.errors.first_name_missing').toString())
      .max(30, t('settings.add_students.errors.first_name_too_long').toString())
      .matches(/^[\p{L}\p{M}'\s.,\-]+$/u, t('invalidFirstname').toString())
      .test(
        'no-empty-spaces',
        t('noTrailingSpaces').toString(),
        value => !value || value.trim() === value
      ),
    lastName: Yup.string()
      .required(t('settings.add_students.errors.last_name_missing').toString())
      .max(40, t('settings.add_students.errors.last_name_too_long').toString())
      .matches(/^[\p{L}\p{M}'\s.,\-]+$/u, t('invalidLastname').toString())
      .test(
        'no-empty-spaces',
        t('noTrailingSpaces').toString(),
        value => !value || value.trim() === value
      ),
    username: Yup.string()
      .required(t('settings.add_students.errors.username_missing').toString())
      .max(20, t('usernameTooLong').toString())
      .matches(/^\S{1,20}$/, t('invalidUsername').toString())
      .test('no-spaces', t('noTrailingSpaces').toString(), value => !value || !/\s/.test(value)),
    password: Yup.string()
      .min(8, t('passwordMinimumLength').toString())
      .required(t('enterNewPassword').toString())
      .matches(PASSWORD_REGEX),
    confirmPassword: Yup.string()
      .required(t('validation.confirm_password_required').toString())
      .oneOf([Yup.ref('password')], t('passwordsDontMatch').toString()),
  });

  const initialValues: LoginValues = {
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: RegistrationSchema,
    onSubmit: (emitSubmit ?? (() => {})) as (
      values: LoginValues,
      formikHelpers: FormikHelpers<LoginValues>
    ) => void,
  });

  const onClear = (event: React.MouseEvent<SVGElement, MouseEvent>, name: string) => {
    formik.setFieldValue(name, '');
    formik.setFieldTouched(name, true, false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    formik.setFieldTouched(event.target.name, true, false);
  };

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleBlur(event);
  };

  return (
    <>
      <form className={classNames.classCodeForm} onSubmit={formik.handleSubmit}>
        <div className={classNames.firstnameAndLastnameFields}>
          <AppInput
            className={classes(styles.formField, {
              [styles.formFieldError]: formik.touched.firstName && !!formik.errors.firstName,
            })}
            inputWrapperClassName={classes(styles.appInputWrapper, {
              [styles.appInputWrapperError]: formik.touched.firstName && !!formik.errors.firstName,
            })}
            id='firstName'
            name='firstName'
            label={t('firstname') ?? ''}
            type='text'
            placeholder={t('firstname') ?? ''}
            value={formik.values?.firstName}
            error={formik.touched.firstName ? t(formik.errors.firstName as string) : undefined}
            allowClear={true}
            emitChange={handleChange}
            emitBlur={handleBlur}
            emitClear={onClear}
            isRequired={true}
          />

          <AppInput
            className={classes(styles.formField, {
              [styles.formFieldError]: formik.touched.lastName && !!formik.errors.lastName,
            })}
            inputWrapperClassName={classes(styles.appInputWrapper, {
              [styles.appInputWrapperError]: formik.touched.lastName && !!formik.errors.lastName,
            })}
            id='lastName'
            name='lastName'
            label={t('lastname') ?? ''}
            type='text'
            placeholder={t('lastname') ?? ''}
            value={formik.values?.lastName}
            error={formik.touched.lastName ? t(formik.errors.lastName as string) : undefined}
            allowClear={true}
            emitChange={handleChange}
            emitBlur={handleBlur}
            emitClear={onClear}
            isRequired={true}
          />
        </div>

        <AppInput
          className={classes(styles.formField, {
            [styles.formFieldError]: formik.touched.username && !!formik.errors.username,
          })}
          inputWrapperClassName={classes(styles.appInputWrapper, {
            [styles.appInputWrapperError]: formik.touched.username && !!formik.errors.username,
          })}
          id='username'
          name='username'
          label={t('username') ?? ''}
          type='text'
          placeholder={t('chooseUsername') ?? ''}
          value={formik.values?.username}
          error={formik.touched.username ? t(formik.errors.username as string) : undefined}
          allowClear={true}
          emitChange={handleChange}
          emitBlur={handleBlur}
          emitClear={onClear}
          isRequired={true}
        />

        <AppInput
          className={classes(styles.formField, {
            [styles.formFieldError]: formik.touched.password && !!formik.errors.password,
          })}
          inputWrapperClassName={classes(styles.appInputWrapper, {
            [styles.appInputWrapperError]: formik.touched.password && !!formik.errors.password,
          })}
          id='password'
          name='password'
          label={t('password') ?? ''}
          type='password'
          placeholder={t('yourPasswordHere') ?? ''}
          value={formik.values?.password}
          error={formik.touched.password ? t(formik.errors.password as string) : undefined}
          allowClear={true}
          emitChange={handleChange}
          emitBlur={handleBlur}
          emitClear={onClear}
          isRequired={true}
          showPasswordRequirements={true}
        />

        <AppInput
          className={classes(styles.formField, {
            [styles.formFieldError]:
              formik.touched.confirmPassword && !!formik.errors.confirmPassword,
          })}
          inputWrapperClassName={classes(styles.appInputWrapper, {
            [styles.appInputWrapperError]:
              formik.touched.confirmPassword && !!formik.errors.confirmPassword,
          })}
          id='confirmPassword'
          name='confirmPassword'
          label={t('confirmPassword') ?? ''}
          type='password'
          placeholder={t('yourPasswordHere') ?? ''}
          value={formik.values?.confirmPassword}
          error={
            formik.touched.confirmPassword ? t(formik.errors.confirmPassword as string) : undefined
          }
          allowClear={true}
          emitChange={handleChange}
          emitBlur={handleBlur}
          emitClear={onClear}
          isRequired={true}
        />

        {!loading && (
          <TheButton
            type='submit'
            disabled={formik.isSubmitting}
            className={styles.submit}
            text={t('next')}
            shouldRecolor={false}
          />
        )}
        {loading && (
          <div className={styles.loadingWrapper}>
            <AppLoaderCircle />
          </div>
        )}
      </form>
    </>
  );
};

export default RegistrationForm;
